import useSWR from "swr";
import { Link } from "react-router-dom";
import { Panel, PanelHeader, Kpi } from "@/components/common/Card";
import { Pill, GradePill, DirectionPill } from "@/components/common/Pill";
import {
  getDashboardStats,
  getSignals,
  getSignalStats,
  getSelectorSummary,
  getSelectorV2Summary,
  signalRMultiple,
  isClosed,
  formatNum,
  formatPct,
  formatSignedPct,
  formatR,
  timeAgo,
} from "@/lib/api";
import { DASH, SIGNALS, SELECTOR, SELECTOR_V2 } from "@/constants/testIds";
import {
  CaretRight,
  Lightning,
  ChartBar,
  Pulse,
  Target,
  Clock,
  Cpu,
  Trophy,
  Funnel,
  CheckCircle,
  Prohibit,
  Eye,
  Flask,
  Pulse as Activity,
} from "@phosphor-icons/react";

const REGIME_COLORS = {
  TRENDING_UP: "bg-[#16a34a]",
  TRENDING_DOWN: "bg-[#dc2626]",
  RANGING: "bg-slate-400",
  HIGH_VOLATILITY: "bg-[#d97706]",
  CHOPPY: "bg-orange-500",
  UNKNOWN: "bg-slate-400",
};

const STRAT_LABEL = {
  trend_pullback: "Trend Pullback",
  breakout_retest: "Breakout Retest",
  range_trading: "Range Trading",
  liquidity_sweep: "Liquidity Sweep",
};

const DistRow = ({ label, count, total, color }) => {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="px-4 py-2.5">
      <div className="flex items-center justify-between text-xs mb-1.5">
        <span className="text-slate-700 tracking-wide">{label}</span>
        <span className="text-slate-600 font-data">
          {count} <span className="text-slate-400">· {pct}%</span>
        </span>
      </div>
      <div className="h-1.5 bg-[#e2e8f0] rounded-sm overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const LifeCell = ({ label, value, accent = "text-slate-900", sub, testid }) => (
  <div
    data-testid={testid}
    className="px-4 py-3 bg-[#f1f5f9] border border-slate-200/70 rounded-sm"
  >
    <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500">
      {label}
    </div>
    <div className={`mt-1.5 text-2xl font-data tracking-tight ${accent}`}>
      {value}
    </div>
    {sub && <div className="mt-0.5 text-[11px] text-slate-400 font-data">{sub}</div>}
  </div>
);

const LifecycleStats = () => {
  const { data: stats } = useSWR("/signals/stats", () => getSignalStats(30), {
    refreshInterval: 30000,
  });
  const { data: all } = useSWR(
    ["/signals", "lifecycle-all"],
    () => getSignals({ limit: 500 }),
    { refreshInterval: 30000 },
  );

  const list = all || [];
  const tp1 = list.filter((s) => s.tp1_hit_at).length;
  const tp2 = list.filter((s) => s.tp2_hit_at).length;
  const tp3 = list.filter((s) => s.tp3_hit_at).length;
  const rValues = list
    .filter(isClosed)
    .map(signalRMultiple)
    .filter((r) => r !== null && !Number.isNaN(r));
  const avgR =
    rValues.length > 0
      ? rValues.reduce((a, b) => a + b, 0) / rValues.length
      : null;

  const winRate = stats?.win_rate;
  const avgPnl = stats?.avg_pnl_percent;

  return (
    <Panel data-testid={DASH.lifecycleStats}>
      <PanelHeader
        title="Lifecycle Performance"
        hint="Closed-trade outcomes · last 30 days"
        action={<Trophy size={14} className="text-slate-500" />}
      />
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2 p-3">
        <LifeCell
          testid="life-win-rate"
          label="Win Rate"
          value={winRate === null || winRate === undefined ? "—" : `${winRate}%`}
          accent="text-[#16a34a]"
          sub={`${stats?.loss_rate ?? "—"}% loss`}
        />
        <LifeCell
          testid="life-closed"
          label="Closed Trades"
          value={stats?.closed_signals ?? "—"}
          accent="text-slate-900"
          sub={`${stats?.active_signals ?? "—"} active`}
        />
        <LifeCell
          testid="life-tp1"
          label="TP1 Hits"
          value={tp1}
          accent="text-[#2563eb]"
          sub={`${stats?.tp1_hit_rate ?? 0}% rate`}
        />
        <LifeCell
          testid="life-tp2"
          label="TP2 Hits"
          value={tp2}
          accent="text-[#2563eb]"
          sub={`${stats?.tp2_hit_rate ?? 0}% rate`}
        />
        <LifeCell
          testid="life-tp3"
          label="TP3 Hits"
          value={tp3}
          accent="text-[#2563eb]"
          sub={`${stats?.tp3_hit_rate ?? 0}% rate`}
        />
        <LifeCell
          testid="life-avg-pnl"
          label="Avg PnL"
          value={formatSignedPct(avgPnl)}
          accent={
            (avgPnl ?? 0) >= 0 ? "text-[#16a34a]" : "text-[#dc2626]"
          }
          sub="Blended · per trade"
        />
        <LifeCell
          testid="life-avg-r"
          label="Avg R"
          value={formatR(avgR)}
          accent={(avgR ?? 0) >= 0 ? "text-[#16a34a]" : "text-[#dc2626]"}
          sub={`${rValues.length} closed`}
        />
      </div>
    </Panel>
  );
};

const COMBO_LABEL = (k) => {
  const [strat, dir] = k.split(":");
  return `${STRAT_LABEL[strat] || strat} · ${dir}`;
};

const SelectorBar = ({ label, count, total, color, testid }) => {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="px-4 py-2" data-testid={testid}>
      <div className="flex items-center justify-between text-xs mb-1.5">
        <span className="text-slate-700 tracking-wide">{label}</span>
        <span className="text-slate-600 font-data">
          {count} <span className="text-slate-400">· {pct}%</span>
        </span>
      </div>
      <div className="h-1.5 bg-[#e2e8f0] rounded-sm overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const StrategySelector = () => {
  const { data } = useSWR("/strategy-selector/summary", getSelectorSummary, {
    refreshInterval: 30000,
  });

  const evaluated = data?.pairs_evaluated ?? 0;
  const noTrade = data?.no_trade_count ?? 0;
  const regimes = data?.regimes_seen || {};
  const regimeTotal = Object.values(regimes).reduce((s, n) => s + n, 0);
  const allowed = Object.entries(data?.eligibility_counts || {});
  const allowedTotal = allowed.reduce((s, [, n]) => s + n, 0);
  const rejected = Object.entries(data?.rejected_by_reason || {});
  const rejectedTotal = rejected.reduce((s, [, n]) => s + n, 0);
  const noTradePct = evaluated > 0 ? Math.round((noTrade / evaluated) * 100) : 0;

  return (
    <Panel data-testid={SELECTOR.shell} className="lg:col-span-12">
      <PanelHeader
        title="Strategy Selector"
        hint="Deterministic regime gating · last evaluation window"
        action={<Funnel size={14} className="text-slate-500" />}
      />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 lg:gap-4 p-3">
        {/* Regime distribution */}
        <div className="lg:col-span-4" data-testid={SELECTOR.regimeDist}>
          <div className="px-4 pt-2 flex items-center justify-between">
            <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500">
              Regimes Seen
            </span>
            <span className="text-[11px] text-slate-400 font-data">
              {evaluated} pairs
            </span>
          </div>
          {regimeTotal === 0 ? (
            <div className="px-4 py-8 text-center text-xs text-slate-400">
              No evaluations yet.
            </div>
          ) : (
            Object.entries(regimes).map(([k, v]) => (
              <SelectorBar
                key={k}
                label={k.replace(/_/g, " ")}
                count={v}
                total={regimeTotal}
                color={REGIME_COLORS[k] || "bg-slate-400"}
              />
            ))
          )}
          <div
            data-testid={SELECTOR.noTrade}
            className="mx-4 mt-3 mb-1 px-4 py-3 bg-orange-50 border border-orange-200/70 rounded-sm flex items-center justify-between"
          >
            <div className="flex items-center gap-2 text-xs text-orange-700 font-bold tracking-wide">
              <Prohibit size={15} weight="bold" /> No-Trade Pairs
            </div>
            <div className="text-right">
              <span className="text-xl font-data text-orange-600">{noTrade}</span>
              <span className="text-[11px] text-orange-400 font-data ml-1.5">
                · {noTradePct}%
              </span>
            </div>
          </div>
        </div>

        {/* Allowed strategies */}
        <div className="lg:col-span-4" data-testid={SELECTOR.allowed}>
          <div className="px-4 pt-2 flex items-center gap-1.5">
            <CheckCircle size={13} weight="bold" className="text-[#16a34a]" />
            <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500">
              Allowed Combos
            </span>
          </div>
          {allowed.length === 0 ? (
            <div className="px-4 py-8 text-center text-xs text-slate-400">
              No eligible strategies.
            </div>
          ) : (
            allowed.map(([k, v]) => (
              <SelectorBar
                key={k}
                label={COMBO_LABEL(k)}
                count={v}
                total={allowedTotal}
                color="bg-[#16a34a]"
              />
            ))
          )}
        </div>

        {/* Blocked / rejection reasons */}
        <div className="lg:col-span-4" data-testid={SELECTOR.blocked}>
          <div className="px-4 pt-2 flex items-center gap-1.5">
            <Prohibit size={13} weight="bold" className="text-[#dc2626]" />
            <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500">
              Top Rejection Reasons
            </span>
          </div>
          <div data-testid={SELECTOR.reasons}>
            {rejected.length === 0 ? (
              <div className="px-4 py-8 text-center text-xs text-slate-400">
                No rejections logged.
              </div>
            ) : (
              rejected.slice(0, 8).map(([reason, count]) => (
                <div
                  key={reason}
                  className="px-4 py-2 flex items-center justify-between gap-3 border-b border-slate-200/50 last:border-0"
                >
                  <span className="text-xs text-slate-600 leading-tight">{reason}</span>
                  <span className="text-xs font-data text-[#dc2626] shrink-0">
                    {count}
                    <span className="text-slate-400 ml-1">
                      ·{" "}
                      {rejectedTotal > 0
                        ? Math.round((count / rejectedTotal) * 100)
                        : 0}
                      %
                    </span>
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </Panel>
  );
};

const StrategySelectorV2 = () => {
  const { data } = useSWR("/strategy-selector-v2/summary", getSelectorV2Summary, {
    refreshInterval: 30000,
  });
  const total = data?.total_decisions ?? 0;
  const allow = data?.v2_allowed_count ?? 0;
  const block = data?.v2_blocked_count ?? 0;
  const observe = data?.v2_observe_only_count ?? 0;
  const agreement = data?.agreement_rate ?? 0;
  const winRate = data?.v2_win_rate;
  const rImp = data?.estimated_r_improvement ?? 0;
  const blockedLosers = data?.v2_blocked_loser_count ?? 0;
  const blockedWinners = data?.v2_blocked_winner_count ?? 0;
  const allowReasons = Object.entries(data?.top_allow_reasons || {});
  const blockReasons = Object.entries(data?.top_block_reasons || {});
  const coverage = data?.coverage_evaluations?.by_strategy || {};
  const coverageRows = Object.entries(coverage);
  const STRAT_LBL = {
    trend_pullback: "Trend Pullback", breakout_retest: "Breakout Retest",
    range_trading: "Range Trading", liquidity_sweep: "Liquidity Sweep",
  };

  return (
    <Panel data-testid={SELECTOR_V2.shell} className="lg:col-span-12">
      <PanelHeader
        title="Strategy Selector V2"
        hint="Advisory recommendations vs the live selector — read-only"
        action={<Flask size={14} className="text-slate-500" />}
      />
      <div className="px-4 pt-3">
        <div
          data-testid={SELECTOR_V2.status}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm bg-amber-50 border border-amber-200 text-amber-700 text-[10px] font-bold tracking-[0.2em] uppercase"
        >
          <Eye size={13} weight="bold" /> Shadow Mode · does not affect live trading
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 p-4">
        <Kpi label="Decisions" value={total} sub="shadow-logged" />
        <Kpi testid={SELECTOR_V2.agreement} label="Agreement" value={`${formatNum(agreement, 1)}%`} sub="with live selector" />
        <Kpi testid={SELECTOR_V2.allowed} label="V2 Allow" value={allow} sub={`${block} block · ${observe} observe`} />
        <Kpi label="V2 Win Rate" value={winRate == null ? "—" : `${formatNum(winRate, 1)}%`} sub="allowed & closed" />
        <Kpi
          testid={SELECTOR_V2.rImprovement}
          label="Est. R Improve"
          value={<span className={rImp >= 0 ? "text-[#16a34a]" : "text-[#dc2626]"}>{formatR(rImp)}</span>}
          sub="if V2 were active"
        />
        <Kpi
          testid={SELECTOR_V2.blocked}
          label="Blocked Outcome"
          value={<span className="text-slate-900">{blockedLosers}<span className="text-slate-400 text-base">/{blockedWinners}</span></span>}
          sub="losers avoided / winners missed"
        />
      </div>

      {/* All-registered-strategy coverage (includes flag-disabled) */}
      <div className="px-4 pb-2" data-testid="selector-v2-coverage">
        <div className="pt-1 flex items-center gap-1.5">
          <Funnel size={13} weight="bold" className="text-slate-500" />
          <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500">
            Coverage · All Registered Strategies
          </span>
        </div>
        <div className="overflow-x-auto mt-1.5">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] tracking-[0.1em] uppercase text-slate-500 border-b border-slate-200/70">
                <th className="text-left font-bold px-3 py-1.5">Strategy</th>
                <th className="text-left font-bold px-3 py-1.5">Flag</th>
                <th className="text-right font-bold px-3 py-1.5">Allow</th>
                <th className="text-right font-bold px-3 py-1.5">Block</th>
                <th className="text-right font-bold px-3 py-1.5">Observe</th>
              </tr>
            </thead>
            <tbody>
              {coverageRows.length === 0 ? (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-xs text-slate-400">No coverage evaluations yet — awaiting next scan.</td></tr>
              ) : coverageRows.map(([strat, v]) => (
                <tr key={strat} className="border-b border-slate-200/50">
                  <td className="px-3 py-1.5 text-slate-800 text-xs">{STRAT_LBL[strat] || strat}</td>
                  <td className="px-3 py-1.5">
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-sm ${v.flag_enabled ? "bg-[#16a34a]/10 text-[#16a34a]" : "bg-slate-200 text-slate-500"}`}>
                      {v.flag_enabled ? "ON" : "OFF"}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-right font-data text-[#16a34a]">{v.ALLOW}</td>
                  <td className="px-3 py-1.5 text-right font-data text-[#dc2626]">{v.BLOCK}</td>
                  <td className="px-3 py-1.5 text-right font-data text-amber-600">{v.OBSERVE_ONLY}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Reason breakdowns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 lg:gap-4 px-4 pb-4">
        <div data-testid={SELECTOR_V2.allowReasons}>
          <div className="pt-2 flex items-center gap-1.5">
            <CheckCircle size={13} weight="bold" className="text-[#16a34a]" />
            <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500">Top ALLOW Reasons</span>
          </div>
          {allowReasons.length === 0 ? (
            <div className="py-6 text-center text-xs text-slate-400">No allow decisions yet.</div>
          ) : allowReasons.map(([r, c]) => (
            <div key={r} className="py-2 flex items-center justify-between gap-3 border-b border-slate-200/50 last:border-0">
              <span className="text-xs text-slate-600 leading-tight">{r}</span>
              <span className="text-xs font-data text-[#16a34a] shrink-0">{c}</span>
            </div>
          ))}
        </div>
        <div data-testid={SELECTOR_V2.blockReasons}>
          <div className="pt-2 flex items-center gap-1.5">
            <Prohibit size={13} weight="bold" className="text-[#dc2626]" />
            <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500">Top BLOCK Reasons</span>
          </div>
          {blockReasons.length === 0 ? (
            <div className="py-6 text-center text-xs text-slate-400">No block decisions yet.</div>
          ) : blockReasons.map(([r, c]) => (
            <div key={r} className="py-2 flex items-center justify-between gap-3 border-b border-slate-200/50 last:border-0">
              <span className="text-xs text-slate-600 leading-tight">{r}</span>
              <span className="text-xs font-data text-[#dc2626] shrink-0">{c}</span>
            </div>
          ))}
        </div>
      </div>
    </Panel>
  );
};

export const Dashboard = () => {
  const { data: stats } = useSWR("/dashboard/stats", getDashboardStats, {
    refreshInterval: 15000,
  });
  const { data: recent } = useSWR(
    ["/signals", "recent"],
    () => getSignals({ limit: 8 }),
    { refreshInterval: 15000 },
  );
  const { data: health } = useSWR("/health", null, { refreshInterval: 15000 });

  const aPlus = stats?.grade_distribution?.["A+"] || 0;
  const pairsScanned = stats?.last_scan?.pairs_scanned ?? "—";
  const lastScanAt = stats?.last_scan?.started_at;
  const apiOk = stats?.scheduler_status?.status !== "error";

  const regimeTotal = Object.values(stats?.regime_distribution || {}).reduce(
    (s, n) => s + n,
    0,
  );
  const stratTotal = (stats?.top_strategies || []).reduce(
    (s, x) => s + x.count,
    0,
  );

  return (
    <div data-testid={DASH.shell} className="flex flex-col gap-6 max-w-[1600px]">
      {/* Page Title */}
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-slate-500 font-bold">
            Control Room
          </div>
          <h1 className="text-3xl md:text-4xl font-heading font-black tracking-tighter text-slate-900 mt-1">
            Dashboard
          </h1>
        </div>
        <div className="text-xs text-slate-500 font-data hidden md:block">
          Auto-refresh · 15s
        </div>
      </div>

      {/* Lifecycle Performance — Phase 2 */}
      <LifecycleStats />

      {/* KPI Bento */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Kpi
          testid={DASH.kpiActive}
          label="Pairs Scanned"
          value={pairsScanned}
          accent="text-slate-900"
          sub="Last full scan"
        />
        <Kpi
          testid={DASH.kpi24h}
          label="Signals 24h"
          value={stats?.signals_24h ?? "—"}
          accent="text-[#2563eb]"
          sub={`7d total · ${stats?.signals_7d ?? 0}`}
        />
        <Kpi
          testid="kpi-a-plus"
          label="A+ Signals"
          value={aPlus}
          accent="text-[#16a34a]"
          sub="Highest grade · 7d"
        />
        <Kpi
          testid={DASH.kpiRejected}
          label="Rejected 24h"
          value={stats?.rejected_24h ?? "—"}
          accent="text-[#dc2626]"
          sub="Failed filters"
        />
        <Kpi
          testid="kpi-api-health"
          label="API Health"
          value={
            <span
              className={apiOk ? "text-[#16a34a]" : "text-[#dc2626]"}
            >
              {health?.weex_public_api === "ok" ? "OK" : "DOWN"}
            </span>
          }
          sub={`Sched · ${health?.scheduler || "…"}`}
        />
        <Kpi
          testid="kpi-mode"
          label="Bot Mode"
          value={
            <span className="capitalize text-[#2563eb]">
              {stats?.mode || "—"}
            </span>
          }
          sub={`Interval · ${stats?.scan_interval_minutes || 5}m`}
        />
      </div>

      {/* Main Bento Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Strategy Selector — Phase 3B */}
        <StrategySelector />
        {/* Strategy Selector V2 — Phase 3B.2 (shadow mode) */}
        <StrategySelectorV2 />
        <Panel
          data-testid={DASH.regimeChart}
          className="lg:col-span-4"
        >
          <PanelHeader
            title="Market Regime Distribution"
            hint="Last 7 days"
            action={<ChartBar size={14} className="text-slate-500" />}
          />
          <div className="py-2 min-h-[200px]">
            {regimeTotal === 0 ? (
              <div className="px-4 py-10 text-center text-xs text-slate-400">
                No signals yet — first scan in progress.
              </div>
            ) : (
              Object.entries(stats?.regime_distribution || {}).map(([k, v]) => (
                <DistRow
                  key={k}
                  label={k.replace(/_/g, " ")}
                  count={v}
                  total={regimeTotal}
                  color={REGIME_COLORS[k] || "bg-slate-400"}
                />
              ))
            )}
          </div>
        </Panel>

        {/* Top strategies */}
        <Panel className="lg:col-span-4" data-testid="dashboard-top-strategies">
          <PanelHeader
            title="Top Strategies"
            hint="By signal count · 7d"
            action={<Target size={14} className="text-slate-500" />}
          />
          <div className="py-2 min-h-[200px]">
            {stratTotal === 0 ? (
              <div className="px-4 py-10 text-center text-xs text-slate-400">
                Awaiting first qualifying signals.
              </div>
            ) : (
              (stats?.top_strategies || []).map((s) => (
                <DistRow
                  key={s.strategy}
                  label={STRAT_LABEL[s.strategy] || s.strategy}
                  count={s.count}
                  total={stratTotal}
                  color="bg-[#2563eb]"
                />
              ))
            )}
          </div>
        </Panel>

        {/* Last scan + Top pairs */}
        <div className="lg:col-span-4 flex flex-col gap-4">
          <Panel data-testid={DASH.lastScanCard}>
            <PanelHeader
              title="Last Scan"
              hint={lastScanAt ? timeAgo(lastScanAt) : "Pending"}
              action={<Clock size={14} className="text-slate-500" />}
            />
            <div className="p-4 grid grid-cols-3 gap-2 text-center">
              <div>
                <div className="text-2xl font-data text-slate-900">
                  {stats?.last_scan?.pairs_scanned ?? "—"}
                </div>
                <div className="text-[10px] tracking-widest uppercase text-slate-500 mt-1">
                  Pairs
                </div>
              </div>
              <div>
                <div className="text-2xl font-data text-[#16a34a]">
                  {stats?.last_scan?.signals_created ?? "—"}
                </div>
                <div className="text-[10px] tracking-widest uppercase text-slate-500 mt-1">
                  Created
                </div>
              </div>
              <div>
                <div className="text-2xl font-data text-[#dc2626]">
                  {stats?.last_scan?.signals_rejected ?? "—"}
                </div>
                <div className="text-[10px] tracking-widest uppercase text-slate-500 mt-1">
                  Rejected
                </div>
              </div>
            </div>
            <div className="px-4 pb-3 flex items-center justify-between text-[11px] text-slate-500 border-t border-slate-200/70 pt-3">
              <span>Status</span>
              <Pill
                tone={
                  stats?.last_scan?.status === "success"
                    ? "success"
                    : stats?.last_scan?.status === "running"
                      ? "info"
                      : stats?.last_scan?.status === "error"
                        ? "error"
                        : "neutral"
                }
              >
                {stats?.last_scan?.status || "idle"}
              </Pill>
            </div>
          </Panel>

          <Panel data-testid={DASH.topPairs}>
            <PanelHeader
              title="Top Pairs"
              hint="By signal count · 7d"
              action={<Activity size={14} className="text-slate-500" />}
            />
            <div className="p-2">
              {(stats?.top_pairs || []).length === 0 ? (
                <div className="px-4 py-6 text-center text-xs text-slate-400">
                  No data yet.
                </div>
              ) : (
                (stats?.top_pairs || []).map((p, i) => (
                  <div
                    key={p.pair}
                    className="flex items-center justify-between px-3 py-2 hover:bg-[#e2e8f0] rounded-sm"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-slate-400 font-data w-5">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="text-sm font-data text-slate-900">
                        {p.pair}
                      </span>
                    </div>
                    <span className="text-xs font-data text-[#2563eb]">
                      {p.count}
                    </span>
                  </div>
                ))
              )}
            </div>
          </Panel>
        </div>

        {/* Recent Signals */}
        <Panel
          data-testid={DASH.recentSignals}
          className="lg:col-span-12"
        >
          <PanelHeader
            title="Recent Signals"
            hint="Newest first · click row for full breakdown"
            action={
              <Link
                to="/signals"
                className="text-[10px] tracking-widest uppercase text-[#2563eb] hover:text-slate-900 flex items-center gap-1"
              >
                All Signals <CaretRight size={10} weight="bold" />
              </Link>
            }
          />
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] tracking-[0.1em] uppercase text-slate-500 border-b border-slate-200/70">
                  <th className="text-left font-bold px-4 py-2">Pair</th>
                  <th className="text-left font-bold px-4 py-2">Dir</th>
                  <th className="text-left font-bold px-4 py-2">Strategy</th>
                  <th className="text-left font-bold px-4 py-2">Grade</th>
                  <th className="text-right font-bold px-4 py-2">Score</th>
                  <th className="text-right font-bold px-4 py-2">Entry</th>
                  <th className="text-right font-bold px-4 py-2">RR</th>
                  <th className="text-right font-bold px-4 py-2">Created</th>
                </tr>
              </thead>
              <tbody>
                {(recent || []).length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-4 py-10 text-center text-xs text-slate-400"
                    >
                      <Lightning
                        size={24}
                        className="mx-auto mb-2 text-slate-400"
                      />
                      No signals yet. Trigger a scan from the top bar.
                    </td>
                  </tr>
                ) : (
                  (recent || []).map((s) => (
                    <tr
                      key={s.id}
                      data-testid={SIGNALS.row(s.id)}
                      className="border-b border-slate-200/70 hover:bg-[#e2e8f0] transition-colors cursor-pointer"
                      onClick={() => (window.location.href = `/signals/${s.id}`)}
                    >
                      <td className="px-4 py-3 font-data text-slate-900">
                        {s.pair}
                      </td>
                      <td className="px-4 py-3">
                        <DirectionPill direction={s.direction} />
                      </td>
                      <td className="px-4 py-3 text-slate-700 text-xs">
                        {STRAT_LABEL[s.strategy] || s.strategy}
                      </td>
                      <td className="px-4 py-3">
                        <GradePill grade={s.grade} />
                      </td>
                      <td className="px-4 py-3 text-right font-data text-slate-900">
                        {formatNum(s.score, 1)}
                      </td>
                      <td className="px-4 py-3 text-right font-data text-slate-700">
                        {formatNum(s.entry)}
                      </td>
                      <td className="px-4 py-3 text-right font-data text-[#2563eb]">
                        {formatNum(s.rr, 2)}
                      </td>
                      <td className="px-4 py-3 text-right text-slate-500 text-xs">
                        {timeAgo(s.created_at)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>
    </div>
  );
};
